import {AddEstimateTreeDto, EstimateTreeDto, UpdateEstimateTreeDto} from "../../shared/dtos/estimate-tree.dto";
import {EstimateTreeOperationBulkUpdateRequestEntryDto, EstimateTreeOperationBulkUpdateResponseEntryDto, EstimateTreeOperationDto, InputEstimateTreeOperationDto, TreeOperationType} from "../../shared/dtos/estimate-tree-operation.dto";
import {NavigateFunction} from "react-router-dom";
import {getUserTokenOrRedirect} from "../../shared/services/auth.service";
import {InputDataType, InputSchema} from "../../shared/services/schema-based-inputs.service";
import {EstimateTreeEquipmentDto, UpdateEstimateTreeEquipmentDto} from "../../shared/dtos/estimate-tree-equipment.dto";
import { EstimateTreeOperationMethodDto, UpdateEstimateTreeOperationMethodDto } from "../../shared/dtos/estimate-tree-operation-method.dto";

export async function createTree(navigate: NavigateFunction, estimateId: number, tree: AddEstimateTreeDto ) {
  const token = await getUserTokenOrRedirect(navigate)
  const createdTree = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(tree),
    }).then(async r => {
      if (!r.ok) {
        throw new Error(`Failed to create tree: ${r.statusText}`);
      }
      return (await r.json()) as EstimateTreeDto
    });
  return createdTree;
}

export async function getTree(navigate: NavigateFunction, estimateId: number, treeId: number): Promise<EstimateTreeDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const tree = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async r => r.ok ? (await r.json()) as EstimateTreeDto : Promise.reject(r));
  return tree;
}

export async function updateTree(navigate: NavigateFunction, estimateId: number, treeId: number, tree: UpdateEstimateTreeDto): Promise<EstimateTreeDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}`,
    {
        method: 'PATCH',
        body: JSON.stringify(tree),
        headers:
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
    })
    .then(async r => r.ok ? (await r.json()) as EstimateTreeDto : Promise.reject(r));

  return response;
}

export async function deleteTree(navigate: NavigateFunction, estimateId: number, treeId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}`,
    {
      method: 'DELETE',
      headers:
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
    });
  if(!response.ok) {
    throw new Error(`Failed to delete tree: ${response.statusText}`);
  }
}

export async function getTreeOperations(navigate: NavigateFunction, estimateId: number, treeId: number): Promise<EstimateTreeOperationDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const operations = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/operations`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async r => r.ok ? (await r.json()) as EstimateTreeOperationDto[] : Promise.reject(r));

  return operations;
}

export async function createTreeOperation(navigate: NavigateFunction, estimateId: number, treeId: number, operation: InputEstimateTreeOperationDto): Promise<EstimateTreeOperationDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const createdOperation = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/operations`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(operation),
  }).then(async r => r.ok ? (await r.json()) as EstimateTreeOperationDto : Promise.reject(r));

  return createdOperation;
}

export async function updateTreeOperation(navigate: NavigateFunction, estimateId: number, treeId: number, operationId: number,  operation: InputEstimateTreeOperationDto): Promise<EstimateTreeOperationDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const createdOperation = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/operations/${operationId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(operation),
    }).then(async r => r.ok ? (await r.json()) as EstimateTreeOperationDto : Promise.reject(r));
  return createdOperation;
}

export async function deleteTreeOperation(navigate: NavigateFunction, estimateId: number, treeId: number, operationId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/operations/${operationId}`,
    {
      method: 'DELETE',
      headers:
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
    })
  if(!response.ok) {
    throw new Error(`Failed to delete operation: ${response.statusText}`);
  }
}

export async function getTreeEquipment(navigate: NavigateFunction, estimateId: number, treeId: number): Promise<EstimateTreeEquipmentDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const equipment = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/equipment`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async r => r.ok ? (await r.json()) as EstimateTreeEquipmentDto[] : Promise.reject(r));

  return equipment;
}

/** [actuals]: also write each machine's actual run time (the server keeps it otherwise). */
export async function updateTreeEquipment(navigate: NavigateFunction, estimateId: number, treeId: number, equipment: UpdateEstimateTreeEquipmentDto[], actuals = false): Promise<EstimateTreeEquipmentDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedEquipment = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/equipment`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(actuals ? {equipment, actuals: true} : {equipment}),
    }).then(async r => r.ok ? (await r.json()) as EstimateTreeEquipmentDto[] : Promise.reject(r));

  return updatedEquipment;
}

export async function getTreeOperationMethods(navigate: NavigateFunction, estimateId: number, treeId: number): Promise<EstimateTreeOperationMethodDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const equipment = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/custom-operation-methods`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async r => r.ok ? (await r.json()) as EstimateTreeOperationMethodDto[] : Promise.reject(r));

  return equipment;
}

export async function updateTreeOperationMethods(navigate: NavigateFunction, estimateId: number, treeId: number, update: UpdateEstimateTreeOperationMethodDto): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/trees/${treeId}/custom-operation-methods`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(update),
    })
}

export async function updateEstimateTreeOperationsBulk(navigate: NavigateFunction, estimateId: number, updates: EstimateTreeOperationBulkUpdateRequestEntryDto[]): Promise<EstimateTreeOperationBulkUpdateResponseEntryDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/bulk/tree-operations`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates),
    });
  if(!response.ok) {
    throw new Error(`Failed to update estimate tree operations bulk: ${response.statusText}`);
  }
  return await response.json() as EstimateTreeOperationBulkUpdateResponseEntryDto[]
}

export const TreeOperationSchema: Record<TreeOperationType, InputSchema[]> = {
  // AC-267 redesign: the amount is categorical (was a percentage), branch
  // type always includes dead wood, and Deadwood Amount is always shown.
  // materialType / deadwoodAmount keep their keys and wire values.
  [TreeOperationType.Thinning]: [
    {name: 'thinningAmount', displayName: 'Thinning Amount', dataType: InputDataType.ShortText, noDefaultValue: true, required: true, options: ['Extra Light', 'Light', 'Standard', 'Heavy', 'Extra Heavy']},
    {name: 'materialType', displayName: 'Branch Type', dataType: InputDataType.ShortText, noDefaultValue: true, required: true, options: ['Live & Dead', 'Dead Only']},
    {name: 'deadwoodAmount', displayName: 'Deadwood Amount', dataType: InputDataType.ShortText, noDefaultValue: true, required: false, options: ['All visible', 'Most', 'Major limbs only', 'Minimal']},
  ],
  // [TreeOperationType.Deadwood_Removal]: [
  //   {name: 'deadwoodPercentage', displayName: 'Deadwood Percentage', dataType: InputDataType.Percentage, required: true},
  // ],
  [TreeOperationType.Custom_Trim_Prune]: [
    {name: 'notes', displayName: 'Custom Trim/Prune Details', dataType: InputDataType.LongText, required: false},
  ],
  // Area-driven (operation-areas.service / OperationAreaChecklist), no flat fields.
  [TreeOperationType.Crown_Reduction]: [],
  [TreeOperationType.Tree_Removal]: [],
  [TreeOperationType.Whole_Stump_Removal]: [],
  // The stump's diameter is the work item's (trunkDiameter, "Stump Diameter"
  // on every client), not the operation's: SQ-630 retired the operation-level
  // stumpDiameterInches that only this form ever wrote.
  [TreeOperationType.Stump_Grinding]: [
    {name: 'grindingDepthInches', displayName: 'Grinding Depth', dataType: InputDataType.Number, required: true, unit: 'inches', options: Array.from(Array(25).keys()).slice(0, 25)},
    {name: 'grindingRadius', displayName: 'Grind Beyond Stump Edge', dataType: InputDataType.ShortText, required: true, noDefaultValue: true, options: ['Stump Only', 'All Surface Roots', ...Array.from(Array(19).keys()).map(i => `${(i * 0.5) + 1} ft`)]},
  ],
  [TreeOperationType.Cabling]: [
    {name: 'notes', displayName: 'Installation Details', dataType: InputDataType.LongText, required: false},
  ],
  [TreeOperationType.Debris_Management]: [
    {name: 'notes', displayName: 'Notes', dataType: InputDataType.LongText, required: false},
  ],
  // Area-driven (operation-areas.service / OperationAreaChecklist), no flat fields.
  [TreeOperationType.Ground_To_Canopy_Clearance]: [],
  [TreeOperationType.Debris_Removal]: [
    {name: 'debrisType', displayName: 'Debris Type', dataType: InputDataType.ShortText, required: true, noDefaultValue: true, options: ['Brush Removal', 'Brush and Log Removal', 'Log Removal', 'Misc Debris Removal']},
  ],
  [TreeOperationType.Misc_Task]: [
    {name: 'descriptiveNote', displayName: 'Task Details', dataType: InputDataType.LongText, required: false},
  ],
  [TreeOperationType.Tree_Health_Care]: [
    {name: 'treatmentDetails', displayName: 'Treatment Details', dataType: InputDataType.LongText, required: true},
  ]
}